import { AppShell } from "@mantine/core";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { navigationDataMappedByParentLink } from "../../constants/navigation";
import { hasPermissionCode } from "../../utils/permission";
import { useIsTenantAdmin } from "../../hooks/useIsTenantAdmin";
import { Sidebar, type SidebarItem } from "@dehub/theme";

// Sections a tenant-admin (tid principal) is allowed in. Route guards (RequireStaff)
// already block STAFF sections; this hides the sidebar too, as defense in depth.
const TENANT_SECTIONS = ["my-billing", "settings"];

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const permissions = useSelector((s: any) => (s.general?.permissions ?? []) as any[]);
  const has = (code?: string) => hasPermissionCode(permissions, code);
  const isTenantAdmin = useIsTenantAdmin();

  const currentPath = location.pathname;
  const currentSection = currentPath.split("/")[1];
  // A tenant-admin never shows STAFF section items (empty perms would otherwise
  // allow-all in hasPermissionCode); only their own surfaces render a sidebar.
  const sectionAllowed = !isTenantAdmin || TENANT_SECTIONS.includes(currentSection);
  const navigation = sectionAllowed
    ? (navigationDataMappedByParentLink[`/${currentSection}`] || []).filter((item) => has(item.requiredPermission))
    : [];

  const items: SidebarItem[] = navigation.map((item) => ({
    key: item.link,
    label: item.title,
    link: item.link,
    icon: item.icon as unknown as SidebarItem["icon"],
    disabled: item.disabled,
  }));

  // Pick the most specific (longest) matching link so an index item (e.g. /my-billing)
  // doesn't stay active on its sub-routes (/my-billing/subscription, /my-billing/fee-policy…).
  const activeKey = items
    .filter((it) => currentPath === it.link || currentPath.startsWith(it.link + "/"))
    .sort((a, b) => b.link.length - a.link.length)[0]?.key;

  return (
    <AppShell.Navbar p={0} style={{ background: "transparent", border: "none" }}>
      <Sidebar items={items} activeKey={activeKey} onPick={(_key, link) => navigate(link)} headerStyle="dark" />
    </AppShell.Navbar>
  );
}
